// frontend/src/components/reports/tableLayout.ts
//
// Geometría de columnas de la tabla de reports: la canaleta, las congeladas y las
// columnas del pie.
//
// Vive aparte del componente por un motivo concreto. La tabla emite sus celdas en
// tres sitios —cabecera, cuerpo y pie— y los tres tienen que emitir el MISMO número
// de columnas. Cuando no coincide, el pie no «falla»: se desplaza en silencio y
// cada total aparece bajo la columna de al lado, que es un error de los que se leen
// como un dato equivocado y no como un fallo de maquetación. Ya pasó al reponer la
// canaleta: se actualizaron cabecera y cuerpo, y el pie se quedó con una menos.
//
// Con el cálculo aquí, en funciones puras, se puede fijar la invariante con un test
// en vez de mirando la pantalla.
import type { TotalsFooterColumn } from '@/components/common/TableTotalsFooter'
import { isNumericCol, type ReportCol } from './reportDefs'
import { DECIMALES_CANTIDAD } from './formato'

/**
 * Ancho de la canaleta: el sitio justo para el marcador y el expansor.
 *
 * Era de 136 a 184 px según el report, porque además alojaba el resumen del
 * bloque. Ese resumen se ha mudado a la primera columna congelada y la canaleta
 * ha quedado en lo que mide su contenido. Lo que gastaba de más se pagaba dos
 * veces: iba dentro del bloque fijo —no se puede apartar desplazándose— y en las
 * sublíneas, que son la mayoría de las filas, no pintaba nada.
 */
export const GUTTER_W = 56

/** Clave de la columna de canaleta. No corresponde a ningún campo del dato. */
export const CANALETA = '__canaleta'

/** Ancho por defecto de una columna que no declara `minW`. */
const ANCHO_POR_DEFECTO = 120

/** Ancho declarado de una columna. */
function ancho(col: ReportCol): number {
  return col.minW ?? ANCHO_POR_DEFECTO
}

/**
 * Desplazamiento izquierdo de cada columna, alineado con `cols`: un número en las
 * congeladas y `undefined` en el resto.
 *
 * Los desplazamientos sólo acumulan el ancho de las CONGELADAS, no el de todas
 * las columnas que quedan a la izquierda. Es lo que hace que unas congeladas no
 * contiguas queden pegadas entre sí al desplazarse, con las de en medio pasando
 * por debajo. Mientras no se desplaza nada, `sticky` no interviene y cada columna
 * se pinta en su sitio natural.
 */
export function frozenOffsets(
  cols: ReportCol[],
  gutter: number = GUTTER_W,
): (number | undefined)[] {
  let acc = gutter
  return cols.map(col => {
    if (!col.frozen) return undefined
    const left = acc
    acc += ancho(col)
    return left
  })
}

/**
 * Etiqueta «Σ N filas» del pie: cuántas celdas colapsa y cuánto mide.
 *
 * Sólo cubre las congeladas que abren la tabla, no todas. Con congeladas NO
 * contiguas —materia prima congela el código de artículo, que va detrás del
 * proveedor— una etiqueta que las abarcara todas mediría más que el bloque fijo
 * del cuerpo, y los totales arrancarían desplazados respecto de sus columnas. Las
 * congeladas que quedan fuera de la etiqueta traen su propio `stickyLeft`.
 */
export function stickyLabel(
  cols: ReportCol[],
  gutter: number = GUTTER_W,
): { span: number; width: number } {
  let n = 0
  let width = gutter
  for (const col of cols) {
    if (!col.frozen) break
    n++
    width += ancho(col)
  }
  return { span: 1 + n, width }
}

/**
 * Columna que aloja los chips del resumen del bloque: la primera congelada.
 *
 * Es la que identifica la fila y la que se sigue viendo al desplazarse, así que un
 * aviso puesto ahí no se pierde de vista. Sin ninguna congelada —el usuario puede
 * ocultarlas desde el menú de columnas— cae en la primera visible, que es lo más
 * parecido que queda.
 */
export function columnaChips(cols: ReportCol[]): string | undefined {
  return (cols.find(c => c.frozen) ?? cols[0])?.key
}

/**
 * Columnas del pie, con la canaleta al frente.
 *
 * La primera entrada NO es un dato: reserva el hueco de la canaleta para que el
 * pie tenga tantas columnas como la cabecera. Sin ella, cada total cae una columna
 * a la izquierda de la suya.
 */
export function buildFooterColumns(
  cols: ReportCol[],
  gutter: number = GUTTER_W,
): TotalsFooterColumn[] {
  const offsets = frozenOffsets(cols, gutter)
  return [
    { key: CANALETA, align: 'left', stickyLeft: 0, width: gutter },
    ...cols.map((c, i) => ({
      key: c.key,
      decimals: c.decimals ?? DECIMALES_CANTIDAD,
      align: isNumericCol(c) ? ('right' as const) : ('left' as const),
      ...(offsets[i] != null ? { stickyLeft: offsets[i], width: c.minW } : {}),
    })),
  ]
}

/**
 * Cuántas columnas emite una fila de la tabla: la canaleta más las de datos.
 *
 * Es el número que tienen que compartir cabecera, cuerpo y pie.
 */
export function totalColumnCount(cols: ReportCol[]): number {
  return cols.length + 1
}
